/**
 * The document's extension point state for the loader's services: the `extensions` and `slots`
 * members of every mount (surface extension points, D7, D8, D9, D10, D16).
 *
 * One board per host document. It holds the live root claim, the one provider of each slot, the
 * fills of each slot and each mount's change listeners, so a root mount and a filling mount of one
 * document meet. `@zion/sdk-surfaces` keeps its own copy of this state for its fakes and its preview
 * (`SlotBoard`, not exported from `./v1`); this module gives the loader the same observable rules:
 *
 * - The routed root that joined the board last holds the live root claim (WS-2 W-7). A new root takes
 *   the claim from an older root that is still on the board. The older root's providers stop with no
 *   last delivery, and its later `provide` refuses `root-only`. A freed claim passes to no other mount.
 * - `has` reads an id as a slot when the caller's row lists it in `fills` or `slots`, or the live
 *   root's row lists it in `slots`. A slot answers `true` only while the live root lists it and has a
 *   provider for it. Any other id answers by its implementers (WS-2 W-15).
 * - A fill value carries only its documented keys, and its data must be plain JSON nested less than
 *   {@link SLOT_FILL_MAX_DEPTH} levels deep. An array with a hole or an own key that is not an index
 *   refuses (WS-2 W-6, R-004, R-014).
 * - Providers and listeners run synchronously inside the call that changed the state, and their
 *   throws never reach the caller (WS-2 W-8).
 *
 * The host's registry reaches the board through one port, {@link ExtensionPointSource}. The kernel
 * SPA passes no source, so every query answers "not present" (D16).
 */
import { EXTENSION_POINT_ID_MAX_LENGTH, EXTENSION_POINT_ID_PATTERN, type SurfaceDeclaration } from '@zion/sdk-contracts';
import {
  SurfaceServiceRefusedError,
  type ExtensionImplementer,
  type SlotFillEntry,
  type SlotFillHandle,
  type SlotFillValue,
  type SlotProvider,
  type SurfaceChangeListener,
  type SurfaceExtensions,
  type SurfaceSlots,
  type SurfaceUnsubscribe,
} from '@zion/sdk-surfaces/v1';
import { disposedError, type MountScope } from './kit.js';
import { isRoutedRoot } from './polarity.js';

/**
 * How deep a fill's JSON data may nest. A value nested this deep or deeper refuses `value-invalid`,
 * so a hostile value cannot exhaust the stack. The same value as `SLOT_FILL_MAX_DEPTH` of the
 * `@zion/sdk-surfaces` slot board, so the loader and the fakes refuse the same fills (R-004).
 */
export const SLOT_FILL_MAX_DEPTH = 32;

/**
 * The host's registry, as the extension point board reads it (the input of `createSurfaceServices`,
 * WS-6). The shell builds one over the registry its loader host resolves from (WS-7). The kernel SPA
 * passes `null` in its place (D16).
 *
 * - `implementers(point)`: the implementers of a well-formed point id that this viewer may mount,
 *   after the same checks as `SurfaceHost.resolve` (permission, bundle URL, same origin): each
 *   embedded, non-root surface whose row lists `point` in `implements`. The board copies, de-duplicates
 *   and sorts the answer itself. A throw reads as `[]`. Pure read.
 * - `declarationOf(appId, surface)`: the declaration of the surface `surface` on the registry row of
 *   `appId`, or `null` when the row or the surface is unknown. The board reads it once per mount, when
 *   the mount joins, for the row's `fills` and `slots`. A throw, or an answer whose `name` is not
 *   `surface`, reads as a row that lists no point. Pure read.
 * - `subscribe(listener)`: call `listener` after any registry change that can change an answer of
 *   `implementers`, such as a registry refetch or a permission change. Returns an idempotent
 *   unsubscribe. The board subscribes once when it is built and unsubscribes at `dispose`.
 */
export interface ExtensionPointSource {
  implementers(point: string): readonly ExtensionImplementer[];
  declarationOf(appId: string, surface: string): SurfaceDeclaration | null;
  subscribe(listener: () => void): () => void;
}

/** One mount's two extension point members. */
export interface ExtensionPointServices {
  readonly extensions: SurfaceExtensions;
  readonly slots: SurfaceSlots;
}

/**
 * The document's extension point board.
 *
 * - `view(scope)` puts the mount of `scope` on the board and answers its `extensions` and `slots`,
 *   each guarded by the scope: after the scope ends, every method throws `SurfaceServiceRefusedError`
 *   `disposed`. The scope's end releases the mount from the board: its fills end (each provider
 *   receives the list without them), its providers stop, it frees the live root claim when it holds
 *   it, and the other mounts' listeners hear once when a provider stopped or the claim ended. A routed
 *   root mount takes the live root claim at `view`. After `dispose`, `view` throws
 *   `SurfaceServiceRefusedError` `disposed`.
 * - `slots.fill` and a fill's `update` check the value, take one structured clone of its data, and
 *   check the clone again. A provider receives only the frozen clone. A getter or a Proxy that answers
 *   a second read with other data, an array with a hole or an own key that is not an index, a value
 *   the structured clone refuses (`DataCloneError`), and a throw from caller code while the board
 *   reads the value each refuse `value-invalid` (R-014). That caller code can also change the board.
 *   When it ends the caller's mount, `fill` refuses `disposed` and the board keeps no fill of the
 *   ended mount. When it removes the fill or ends its mount, `update` refuses `disposed` and delivers
 *   nothing more (R-020).
 * - `slots.provide` reads the provider's `change` once, and that read can run caller code. After the
 *   read the board checks the mount, the live root claim and the slot's provider again: an ended
 *   mount refuses `disposed`, a lost claim `root-only`, and a slot that gained a provider
 *   `value-invalid`. The board keeps no provider of an ended or replaced root (R-020).
 * - `dispose()` unsubscribes from the source. It ends no mount; the provider releases every mount
 *   before it. A second call is a no-op.
 */
export interface ExtensionPointBoard {
  view(scope: MountScope): ExtensionPointServices;
  dispose(): void;
}

type FillListener = (payload: unknown, itemIndex: number | null) => void;

interface MemberState {
  readonly appId: string;
  readonly fills: readonly string[];
  readonly slots: readonly string[];
  readonly listeners: Set<SurfaceChangeListener>;
  readonly ownFills: Set<FillRecord>;
  /** Set once, when the mount's scope ends and `release` takes the mount off the board. */
  released: boolean;
}

interface FillRecord {
  readonly fillId: string;
  readonly point: string;
  readonly owner: MemberState;
  readonly listeners: Map<string, Set<FillListener>>;
  readonly emit: (output: string, payload: unknown, itemIndex: number | null) => void;
  value: SlotFillValue;
  live: boolean;
}

interface ProviderRecord {
  readonly owner: MemberState;
  readonly provider: SlotProvider;
}

const POINT_ID = new RegExp(EXTENSION_POINT_ID_PATTERN);

/** The document-wide fill counter: a fill id is unique across every board of the document. */
let fillCounter = 0;

/** True for an extension point id: a string of at most 96 characters that matches the grammar. Pure. */
function isPointId(value: unknown): value is string {
  return typeof value === 'string' && value.length <= EXTENSION_POINT_ID_MAX_LENGTH && POINT_ID.test(value);
}

/** The well-formed point ids of a row list, frozen; `[]` for an absent list. Pure. */
function pointIdsOf(list: readonly unknown[] | undefined): readonly string[] {
  return Object.freeze((list ?? []).filter(isPointId));
}

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const proto: unknown = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/**
 * True for a dense array whose own keys are its indices `0` to `length - 1` and `length`, and no
 * other key (R-014). The structured clone copies an array's own enumerable keys that are not an
 * index, so an extra key could carry a `Map` or deep data past the element check. A hole, an extra
 * string key (enumerable or not) and a symbol key each answer `false`, the rule of the SDK board's
 * `hasOnlyIndexKeys` in `@zion/sdk-surfaces` (`v1/slot-board.ts`). The two bodies are the same
 * text, so the fake, the vite preview and this loader refuse one set of fills. Reads
 * `Reflect.ownKeys`, so a Proxy's `ownKeys` trap runs here and its throw reaches the caller.
 */
function hasOnlyIndexKeys(array: readonly unknown[]): boolean {
  // `length` is the one own key of every array that is not an index; it follows the indices. Key
  // `i` must be `String(i)` below `length` and `length` at and after it, and own keys are unique,
  // so a missing or an extra key fails one position. No count check decides more.
  return Reflect.ownKeys(array).every((key, index) => key === (index < array.length ? String(index) : 'length'));
}

/**
 * True for plain JSON data: `null`, a boolean, a string, a finite number, a dense array of plain
 * JSON data with no own key other than its indices and `length`, or an object with an `Object` or
 * `null` prototype whose own enumerable values are plain JSON data, nested less than
 * {@link SLOT_FILL_MAX_DEPTH} levels deep. A cycle answers `false`. Pure.
 */
function isPlainJson(value: unknown, depth: number, path: Set<object>): boolean {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return true;
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value !== 'object' || depth >= SLOT_FILL_MAX_DEPTH || path.has(value)) return false;
  if (Array.isArray(value) ? !hasOnlyIndexKeys(value) : !isRecord(value)) return false;
  path.add(value);
  const values: readonly unknown[] = Array.isArray(value) ? value : Object.values(value);
  const ok = values.every((entry) => isPlainJson(entry, depth + 1, path));
  path.delete(value);
  return ok;
}

/** A copy of plain JSON data in a box, so that a `null` datum stays apart from "not plain JSON". */
interface PlainCopy {
  readonly copy: unknown;
}

/**
 * The frozen copy of the caller's plain JSON data that a provider may receive, or `null` when the
 * data is not plain JSON. The caller's value is checked first, so a class instance, a `Map` or a
 * cycle refuses. Then one structured clone is taken, and the clone is checked again: a getter or a
 * Proxy can answer the second read with other data, so the check of the clone decides what reaches
 * the provider. A clone that throws (a Proxy, data the structured clone algorithm refuses, a stack
 * overflow) answers `null`. Only the clone is frozen, so the caller's value stays as it was.
 *
 * @throws What caller code throws while the first check reads the value (a getter, a Proxy trap).
 *   `checkValue` turns that throw into a `value-invalid` refusal.
 */
function plainCopy(value: unknown): PlainCopy | null {
  if (!isPlainJson(value, 0, new Set())) return null;
  let copy: unknown;
  try {
    copy = structuredClone(value);
  } catch {
    return null;
  }
  return isPlainJson(copy, 0, new Set()) ? { copy: deepFreeze(copy) } : null;
}

/** Freeze `value` and every object and array inside it. Returns `value`. */
function deepFreeze<T>(value: T): T {
  if (typeof value === 'object' && value !== null) {
    for (const entry of Object.values(value)) deepFreeze(entry);
    Object.freeze(value);
  }
  return value;
}

/** True when every own key of `value` is one of `allowed`. */
function onlyKeys(value: Readonly<Record<string, unknown>>, allowed: readonly string[]): boolean {
  return Object.keys(value).every((key) => allowed.includes(key));
}

/** Run `call`; a throw is reported to `console.error` and never reaches the caller. */
function contained(what: string, call: () => void): void {
  try {
    call();
  } catch (error) {
    console.error(`[surface-services] ${what} threw`, error);
  }
}

/** Code-unit order of two strings. */
function compareCodeUnits(a: string, b: string): number {
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

/**
 * The source's implementer answer as the board answers it: frozen `{ app, surface }` copies, one per
 * pair, sorted by app, then by surface, by code unit, in a new frozen array. Pure.
 */
function normaliseImplementers(answer: readonly ExtensionImplementer[]): readonly ExtensionImplementer[] {
  const seen = new Set<string>();
  const out: ExtensionImplementer[] = [];
  for (const { app, surface } of answer) {
    const key = JSON.stringify([app, surface]);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(Object.freeze({ app, surface }));
  }
  out.sort((a, b) => compareCodeUnits(a.app, b.app) || compareCodeUnits(a.surface, b.surface));
  return Object.freeze(out);
}

/**
 * Build the board over the host's registry.
 *
 * @param given The registry port, or `null` for a host with no point support (D16). With `null`,
 *   `has` answers `false`, `list` answers `[]`, every `fill` refuses `value-invalid`, and `provide`
 *   refuses `root-only`, or `value-invalid` for the live root, because no row lists a point. A
 *   JavaScript caller, or a caller built against an older `SurfaceServicesDeps`, can pass
 *   `undefined`: the board reads it as `null`, so a missing source never stops the host boot.
 * @throws What `source.subscribe` throws: a host defect.
 */
export function createExtensionPointBoard(given: ExtensionPointSource | null): ExtensionPointBoard {
  // `?? null` folds an `undefined` from an untyped caller into the documented "no source" arm.
  const source: ExtensionPointSource | null = given ?? null;
  const members = new Set<MemberState>();
  const fills = new Map<string, FillRecord[]>();
  const providers = new Map<string, ProviderRecord>();
  let root: MemberState | null = null;
  let ended = false;

  const refuse = (reason: 'root-only' | 'value-invalid' | 'disposed', detail: string): SurfaceServiceRefusedError =>
    new SurfaceServiceRefusedError(reason, detail);

  const announce = (): void => {
    for (const member of Array.from(members)) {
      for (const listener of Array.from(member.listeners)) contained('an extensions listener', listener);
    }
  };

  const stopSource = source === null ? null : source.subscribe(announce);

  const implementers = (point: string): readonly ExtensionImplementer[] => {
    if (source === null || !isPointId(point)) return Object.freeze([]);
    try {
      return normaliseImplementers(source.implementers(point));
    } catch {
      // A throwing registry reads as no implementer: refuse over accept (F3).
      return Object.freeze([]);
    }
  };

  const declarationOf = (appId: string, surface: string): SurfaceDeclaration | null => {
    try {
      const decl = source?.declarationOf(appId, surface) ?? null;
      return decl !== null && decl.name === surface ? decl : null;
    } catch {
      return null;
    }
  };

  const entries = (point: string): readonly SlotFillEntry[] =>
    Object.freeze(
      (fills.get(point) ?? []).map((record) =>
        Object.freeze({ fillId: record.fillId, app: record.owner.appId, value: record.value, emit: record.emit }),
      ),
    );

  const deliver = (point: string): void => {
    const record = providers.get(point);
    if (record === undefined) return;
    const list = entries(point);
    contained('a slot provider', () => record.provider.change(list));
  };

  /** Drop every provider `owner` gave, with no last delivery. Answers whether one was dropped. */
  const stopProviders = (owner: MemberState): boolean => {
    let stopped = false;
    for (const [point, record] of Array.from(providers)) {
      if (record.owner !== owner) continue;
      providers.delete(point);
      stopped = true;
    }
    return stopped;
  };

  const removeFill = (record: FillRecord): void => {
    if (!record.live) return;
    record.live = false;
    record.listeners.clear();
    record.owner.ownFills.delete(record);
    // A live record is always in its point's list: `fill` adds it, and only this call removes it.
    const list = fills.get(record.point)!;
    list.splice(list.indexOf(record), 1);
    if (list.length === 0) fills.delete(record.point);
    deliver(record.point);
  };

  /** End one mount on the board. The mount's scope runs it once, at the scope's end. */
  const release = (state: MemberState): void => {
    state.released = true;
    state.listeners.clear();
    members.delete(state);
    for (const record of Array.from(state.ownFills)) removeFill(record);
    let changed = stopProviders(state);
    if (root === state) {
      root = null;
      changed = true;
    }
    if (changed) announce();
  };

  const has = (state: MemberState, point: string): boolean => {
    if (!isPointId(point)) return false;
    if (root !== null && root.slots.includes(point)) return providers.has(point);
    if (state.fills.includes(point) || state.slots.includes(point)) return false;
    return implementers(point).length > 0;
  };

  const invalidFill = (): SurfaceServiceRefusedError =>
    refuse('value-invalid', 'slots.fill takes an item list or a surface of this app');

  /** The checked, frozen copy of a fill value, or a `value-invalid` refusal. Reads caller code. */
  const checkCallerValue = (state: MemberState, point: string, value: unknown): SlotFillValue => {
    if (!isRecord(value)) throw invalidFill();
    if (value['kind'] === 'item') {
      const items = value['items'];
      if (!onlyKeys(value, ['kind', 'items'])) throw invalidFill();
      // The array check reads the copy, so a second read of `items` cannot swap the type.
      const checked = plainCopy(items);
      if (checked === null || !Array.isArray(checked.copy)) throw invalidFill();
      return Object.freeze({ kind: 'item', items: checked.copy });
    }
    if (value['kind'] !== 'surface' || !onlyKeys(value, ['kind', 'surface', 'input'])) throw invalidFill();
    const surface = value['surface'];
    const input = value['input'];
    if (typeof surface !== 'string') throw invalidFill();
    if (!implementers(point).some((entry) => entry.app === state.appId && entry.surface === surface)) throw invalidFill();
    if (input === undefined) return Object.freeze({ kind: 'surface', surface });
    const checked = plainCopy(input);
    if (checked === null) throw invalidFill();
    return Object.freeze({ kind: 'surface', surface, input: checked.copy });
  };

  /**
   * The checked, frozen copy of a fill value, or a `value-invalid` refusal. The board reads the
   * caller's value through getters and Proxy traps, so caller code runs here. A throw from that code
   * becomes the `value-invalid` refusal: `slots.fill` and `update` throw `SurfaceServiceRefusedError`
   * and no other error of their own.
   */
  const checkValue = (state: MemberState, point: string, value: unknown): SlotFillValue => {
    try {
      return checkCallerValue(state, point, value);
    } catch (error) {
      if (error instanceof SurfaceServiceRefusedError) throw error;
      throw invalidFill();
    }
  };

  const fill = (state: MemberState, point: string, value: SlotFillValue): SlotFillHandle => {
    if (!isPointId(point)) throw refuse('value-invalid', 'slots.fill takes an extension point id');
    if (!state.fills.includes(point)) throw refuse('value-invalid', 'slots.fill takes a slot this surface lists in fills');
    const checked = checkValue(state, point, value);
    // Caller code ran inside `checkValue` and can end this mount there (R-020). A fill of an ended
    // mount would stay on the board after `release` ran, so it refuses as the next call would.
    if (state.released) throw disposedError('fill');
    fillCounter += 1;
    const listeners = new Map<string, Set<FillListener>>();
    const record: FillRecord = {
      fillId: `fill-${fillCounter}`,
      point,
      owner: state,
      listeners,
      value: checked,
      live: true,
      emit: (output, payload, itemIndex) => {
        if (!record.live) return;
        let copy: unknown;
        try {
          copy = structuredClone(payload);
        } catch {
          // A payload that cannot cross as a structured clone is dropped (SlotFillEntry.emit).
          return;
        }
        for (const listener of Array.from(listeners.get(output) ?? [])) {
          contained('a slot fill listener', () => listener(structuredClone(copy), itemIndex));
        }
      },
    };
    const list = fills.get(point) ?? [];
    list.push(record);
    fills.set(point, list);
    state.ownFills.add(record);
    deliver(point);
    return Object.freeze({
      update: (next: SlotFillValue) => {
        if (!record.live) throw refuse('disposed', 'the slot fill ended');
        const checked = checkValue(state, point, next);
        // Caller code inside `checkValue` can remove this fill or end its mount (R-020).
        if (!record.live) throw refuse('disposed', 'the slot fill ended');
        record.value = checked;
        deliver(point);
      },
      remove: () => removeFill(record),
      on: (output: string, listener: FillListener) => {
        if (!record.live) return () => undefined;
        const set = listeners.get(output) ?? new Set<FillListener>();
        set.add(listener);
        listeners.set(output, set);
        return () => {
          set.delete(listener);
        };
      },
    });
  };

  const provide = (state: MemberState, point: string, provider: SlotProvider): SurfaceUnsubscribe => {
    if (root !== state) throw refuse('root-only', 'slots.provide is the live root shell one');
    if (!isPointId(point)) throw refuse('value-invalid', 'slots.provide takes an extension point id');
    if (!state.slots.includes(point)) throw refuse('value-invalid', 'slots.provide takes a slot this root lists in slots');
    if (providers.has(point)) throw refuse('value-invalid', 'slots.provide takes one provider per slot');
    const change: unknown = typeof provider === 'object' && provider !== null ? Reflect.get(provider, 'change') : undefined;
    if (typeof change !== 'function') throw refuse('value-invalid', 'slots.provide takes an object with a change function');
    // The read of `change` runs caller code, a getter or a Proxy trap. That code can end this mount,
    // let a newer root take the claim, or provide this slot first, so the three checks run again
    // before the board keeps the provider (R-020).
    if (state.released) throw disposedError('provide');
    if (root !== state) throw refuse('root-only', 'slots.provide is the live root shell one');
    if (providers.has(point)) throw refuse('value-invalid', 'slots.provide takes one provider per slot');
    const record: ProviderRecord = { owner: state, provider };
    providers.set(point, record);
    deliver(point);
    announce();
    return () => {
      if (providers.get(point) !== record) return;
      providers.delete(point);
      announce();
    };
  };

  return {
    view(scope) {
      if (ended) throw refuse('disposed', 'the extension point board ended');
      const { request } = scope;
      const routedRoot = isRoutedRoot(scope);
      const decl =
        request.placement.kind === 'system' || request.surface === null ? null : declarationOf(request.appId, request.surface);
      const state: MemberState = {
        appId: request.appId,
        fills: routedRoot ? Object.freeze([]) : pointIdsOf(decl?.fills),
        slots: routedRoot ? pointIdsOf(decl?.slots) : Object.freeze([]),
        listeners: new Set(),
        ownFills: new Set(),
        released: false,
      };
      members.add(state);
      if (routedRoot) {
        const previous = root;
        root = state;
        if (previous !== null) stopProviders(previous);
        announce();
      }
      scope.own(() => release(state));
      const extensions: SurfaceExtensions = scope.guard({
        has: (point: string) => has(state, point),
        list: (point: string) => implementers(point),
        subscribe: (listener: SurfaceChangeListener) => {
          const entry: SurfaceChangeListener = () => listener();
          state.listeners.add(entry);
          return () => {
            state.listeners.delete(entry);
          };
        },
      });
      const slots: SurfaceSlots = scope.guard({
        fill: (point: string, value: SlotFillValue) => fill(state, point, value),
        provide: (point: string, provider: SlotProvider) => provide(state, point, provider),
      });
      return Object.freeze({ extensions, slots });
    },
    dispose() {
      if (ended) return;
      ended = true;
      stopSource?.();
    },
  };
}
